import { useEffect, useMemo, useState } from "react";
import classes from "../stats/Statistics.module.css";
import { Interval } from "../navigation/tabs/Tabs";
import { subDays } from "date-fns";
import { Layouts, Responsive, WidthProvider } from "react-grid-layout";
import { Graph, GraphType } from "../graph/Graph";
import { RutxAuth } from "../stats/maintenance/rutxauth/RutxAuth";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faInfoCircle } from "@fortawesome/free-solid-svg-icons";
import ReactTooltip from "react-tooltip";

const ResponsiveReactGridLayout = WidthProvider(Responsive);

const InternetAccess = () => {
  const { startPeriod, endPeriod } = useMemo(() => {
    const start = subDays(new Date(), 6);
    start.setHours(0, 0, 0, 0);

    return {
      startPeriod: start.getTime(),
      endPeriod: Date.now(),
    };
  }, []);

  const [startPeriodTimestamp] = useState<number>(startPeriod);
  const [endPeriodTimestamp] = useState<number>(endPeriod);
  const [interval] = useState<Interval>(Interval.DAY);

  // Reload tooltip is required to get it 100% functional
  useEffect(() => {
    ReactTooltip.rebuild();
  }, []);

  /**
   * Layout strategy:
   * - lg/md (12 cols): 3 cards on first row (4 + 4 + 4), then 1 full-width card
   * - sm (6 cols): 2 cards per row, then full-width
   * - xs (4 cols): stacked full-width
   * - xxs (2 cols): stacked full-width
   */
  const layouts: Layouts = useMemo(
    () => ({
      lg: [
        { i: "wifi_connections_count", x: 0, y: 0, w: 4, h: 3, static: true },
        { i: "sessions_count_evolution", x: 4, y: 0, w: 4, h: 3, static: true },
        { i: "vehicles_count_evolution", x: 8, y: 0, w: 4, h: 3, static: true },
        { i: "total_data_consumption", x: 0, y: 3, w: 12, h: 3, static: true },
      ],
      md: [
        { i: "wifi_connections_count", x: 0, y: 0, w: 4, h: 3, static: true },
        { i: "sessions_count_evolution", x: 4, y: 0, w: 4, h: 3, static: true },
        { i: "vehicles_count_evolution", x: 8, y: 0, w: 4, h: 3, static: true },
        { i: "total_data_consumption", x: 0, y: 3, w: 12, h: 3, static: true },
      ],
      sm: [
        { i: "wifi_connections_count", x: 0, y: 0, w: 3, h: 3, static: true },
        { i: "sessions_count_evolution", x: 3, y: 0, w: 3, h: 3, static: true },
        { i: "vehicles_count_evolution", x: 0, y: 3, w: 3, h: 3, static: true },
        { i: "total_data_consumption", x: 0, y: 6, w: 6, h: 3, static: true },
      ],
      xs: [
        { i: "wifi_connections_count", x: 0, y: 0, w: 4, h: 3, static: true },
        { i: "sessions_count_evolution", x: 0, y: 3, w: 4, h: 3, static: true },
        { i: "vehicles_count_evolution", x: 0, y: 6, w: 4, h: 3, static: true },
        { i: "total_data_consumption", x: 0, y: 9, w: 4, h: 3, static: true },
      ],
      xxs: [
        { i: "wifi_connections_count", x: 0, y: 0, w: 2, h: 3, static: true },
        { i: "sessions_count_evolution", x: 0, y: 3, w: 2, h: 3, static: true },
        { i: "vehicles_count_evolution", x: 0, y: 6, w: 2, h: 3, static: true },
        { i: "total_data_consumption", x: 0, y: 9, w: 2, h: 3, static: true },
      ],
    }),
    []
  );

  return (
    <>
      <ResponsiveReactGridLayout
        rowHeight={100}
        cols={{ lg: 12, md: 12, sm: 6, xs: 4, xxs: 2 }}
        layouts={layouts}
        isDraggable={false}
        isResizable={false}
      >
        <div className={classes.ChartContainer} key="wifi_connections_count">
          <label>
            Évolution du nombre de sessions{" "}
            <span
              data-event="click"
              data-text-color="#ffffff"
              data-place="bottom"
              data-border={true}
              data-border-color="#000000"
              data-background-color="black"
              data-multiline={true}
              data-tip={"Représente le nombre de connexions total avec saisie d'email"}
            >
              <FontAwesomeIcon icon={faInfoCircle} />
            </span>
          </label>

          <div className={classes.ChartTitleSeparator} />
          <div className={classes.ChartGraphContainer}>
            <div className={classes.StatisticsGroupContent}>
              <Graph
                preventUpdateAnimations={false}
                graphType={GraphType.WIFI_CONNECTIONS_COUNT_EVOLUTION}
                startDatePeriodTimestamp={startPeriodTimestamp}
                interval={interval}
                nodePath={""}
                endDatePeriodTimestamp={endPeriodTimestamp}
              />
            </div>
          </div>
        </div>

        <div className={classes.ChartContainer} key="sessions_count_evolution">
          <label>
            Évolution du nombre de visiteurs uniques{" "}
            <span
              data-event="click"
              data-text-color="#ffffff"
              data-place="bottom"
              data-border={true}
              data-border-color="#000000"
              data-background-color="black"
              data-multiline={true}
              data-tip={
                "Représente le nombre de connexions avec saisie d'email d'appareils (adresse MAC) différents"
              }
            >
              <FontAwesomeIcon icon={faInfoCircle} />
            </span>
          </label>

          <div className={classes.ChartTitleSeparator} />
          <div className={classes.ChartGraphContainer}>
            <Graph
              preventUpdateAnimations={false}
              graphType={GraphType.SESSIONS_COUNT_EVOLUTION}
              startDatePeriodTimestamp={startPeriodTimestamp}
              interval={interval}
              nodePath={""}
              endDatePeriodTimestamp={endPeriodTimestamp}
            />
          </div>
        </div>

        <div className={classes.ChartContainer} key="vehicles_count_evolution">
          <label>
            Évolution du nombre de véhicules{" "}
            <span
              data-event="click"
              data-text-color="#ffffff"
              data-place="bottom"
              data-border={true}
              data-border-color="#000000"
              data-background-color="black"
              data-multiline={true}
              data-tip={"Représente le nombre véhicules ayant demarré par jours ( > 2min)"}
            >
              <FontAwesomeIcon icon={faInfoCircle} />
            </span>
          </label>

          <div className={classes.ChartTitleSeparator} />
          <div className={classes.ChartGraphContainer}>
            <div className={classes.StatisticsGroupContent}>
              <Graph
                preventUpdateAnimations={false}
                graphType={GraphType.VEHICLES_COUNT_EVOLUTION}
                startDatePeriodTimestamp={startPeriodTimestamp}
                interval={interval}
                nodePath={""}
                endDatePeriodTimestamp={endPeriodTimestamp}
              />
            </div>
          </div>
        </div>

        {/* Commented out for now

        <div className={classes.ChartContainer} key={"monitor_hits_count_evolution"}>
          <label>
            Evolution du nombre de hits{" "}
            <span
              data-event={"click"}
              data-text-color={"#ffffff"}
              data-place={"bottom"}
              data-border={true}
              data-border-color={"#000000"}
              data-background-color={"black"}
              data-multiline={true}
              data-tip={
                "Représente le nombre de remontées de données de monitoring de l'ensemble des véhicules sur les 7 derniers jours"
              }
            >
              <FontAwesomeIcon icon={faInfoCircle} />
            </span>
          </label>

          <div className={classes.ChartTitleSeparator} />
          <div className={classes.ChartGraphContainer}>
            <div className={classes.StatisticsGroupContent}>
              <Graph
                preventUpdateAnimations={false}
                graphType={GraphType.MONITOR_HITS_COUNT_EVOLUTION}
                startDatePeriodTimestamp={startPeriodTimestamp}
                interval={Interval.HOUR}
                nodePath={""}
                endDatePeriodTimestamp={endPeriodTimestamp}
              />
            </div>
          </div>
        </div>

        <div className={classes.ChartContainer} key={"rutx_auth_messages"}>
          <label>
            Connexions administration du routeur{" "}
            <span
              data-event={"click"}
              data-text-color={"#ffffff"}
              data-place={"bottom"}
              data-border={true}
              data-border-color={"#000000"}
              data-background-color={"black"}
              data-multiline={true}
              data-tip={"Nombre de tentatives de connexions/status au routeur RUTX sur les 7 derniers jours"}
            >
              <FontAwesomeIcon icon={faInfoCircle} />
            </span>
          </label>

          <div className={classes.ChartTitleSeparator} />
          <div className={classes.ChartGraphContainer}>
            <div className={classes.StatisticsGroupContent}>
              <RutxAuth
                area={undefined}
                startDateTimestamp={startPeriodTimestamp}
                nodePath={""}
                endDateTimestamp={endPeriodTimestamp}
              />
            </div>
          </div>
        </div>

        <div className={classes.ChartContainer} key={"nb_hits_rutx_hotspot"}>
          <label>Traffic réseau (nombre de paquets)</label>
          <div className={classes.ChartTitleSeparator} />
          <div className={classes.ChartGraphContainer}>
            <div className={classes.StatisticsGroupContent}>
              <Graph
                preventUpdateAnimations={false}
                graphType={GraphType.NETWORK_HITS_PER_NET}
                startDatePeriodTimestamp={startPeriodTimestamp}
                interval={interval}
                nodePath={""}
                endDatePeriodTimestamp={endPeriodTimestamp}
              />
            </div>
          </div>
        </div>

        */}

        <div className={classes.ChartContainer} key="total_data_consumption">
          <label>
            Consommation data mobile totale{" "}
            <span
              data-event="click"
              data-text-color="#ffffff"
              data-place="bottom"
              data-border={true}
              data-border-color="#000000"
              data-background-color="black"
              data-multiline={true}
              data-tip={
                "Représente l'évolution de la quantité de données mobiles consommée sur les 7 derniers jours pour l'ensemble du parc de véhicules"
              }
            >
              <FontAwesomeIcon icon={faInfoCircle} />
            </span>
          </label>

          <div className={classes.ChartTitleSeparator} />
          <div className={classes.ChartGraphContainer}>
            <div className={classes.StatisticsGroupContent}>
              <Graph
                preventUpdateAnimations={false}
                graphType={GraphType.TOTAL_DATA_USAGE_EVOLUTION}
                startDatePeriodTimestamp={startPeriodTimestamp}
                interval={interval}
                nodePath={""}
                endDatePeriodTimestamp={endPeriodTimestamp}
              />
            </div>
          </div>
        </div>

        {/*}
        <div className={classes.ChartContainer} key={"nginx_requests_per_statuses"}>
          <label>Évolution des codes HTTP</label>
          <div className={classes.ChartTitleSeparator}/>
          <div className={classes.ChartGraphContainer}>
            <div className={classes.StatisticsGroupContent}>
              <Graph
                preventUpdateAnimations={false}
                graphType={GraphType.NGINX_STATUS_COUNT_EVOLUTION}
                startDatePeriodTimestamp={startPeriodTimestamp}
                interval={interval}
                nodePath={""}
                endDatePeriodTimestamp={endPeriodTimestamp}
              />
            </div>
          </div>
        </div>
        */}
      </ResponsiveReactGridLayout>

      <ReactTooltip />
    </>
  );
};

export default InternetAccess;
